import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ListGroup, Button, Form, Alert } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getPortfolios, updatePortfolio, updateAccount, getSettings, getAllAccountsWithAggregate } from '../services/api';
import { useMarketCondition } from '../contexts/MarketConditionContext';
import { getMarketConditionOption } from '../utils/marketConditions';
import { subscribeToTickerUpdates } from '../services/socketService';

function NavigationPanel({ mobileVisible = false }) {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryLoading, setRetryLoading] = useState(false);
  const [accountsData, setAccountsData] = useState(null); // Store accounts page data when available - persists across page navigation for consistent calculations
  const [aggregateAllTimeHigh, setAggregateAllTimeHigh] = useState(null); // Store aggregate all-time high data
  const [collapsed, setCollapsed] = useState(false);
  const [pinned, setPinned] = useState(() => {
    const savedPinned = localStorage.getItem('navPanelPinned');
    return savedPinned !== null ? JSON.parse(savedPinned) : true;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navPanelRef = useRef(null);
  const { marketCondition } = useMarketCondition();
  
  // Edit states
  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  // Collapsed portfolios state
  const [collapsedPortfolios, setCollapsedPortfolios] = useState({});
  // Collapsible extra navigation items under Accounts
  const [showExtraNavigationItems, setShowExtraNavigationItems] = useState(false);
  
  // Navigation display setting
  const [showPortfoliosInNav, setShowPortfoliosInNav] = useState(true);

  // Ensure hidden submenu is no longer accessible
  useEffect(() => {
    try { localStorage.setItem('showExtraNavigationItems', JSON.stringify(false)); } catch (e) {}
    setShowExtraNavigationItems(false);
  }, []);
  
  // Detect mobile screens
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // no-op
  
  // Retrieve collapsed and pinned state from localStorage on initial render
  useEffect(() => {
    const savedCollapsed = localStorage.getItem('navPanelCollapsed');
    if (savedCollapsed !== null) {
      setCollapsed(JSON.parse(savedCollapsed));
    }
    
    const savedPinned = localStorage.getItem('navPanelPinned');
    if (savedPinned !== null) {
      setPinned(JSON.parse(savedPinned));
    } else {
      setPinned(true);
    }
    
    // Retrieve collapsed portfolios state
    const savedCollapsedPortfolios = localStorage.getItem('collapsedPortfolios');
    if (savedCollapsedPortfolios !== null) {
      setCollapsedPortfolios(JSON.parse(savedCollapsedPortfolios));
    }
  }, []);

  // Save collapsed state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('navPanelCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);
  
  // Save pinned state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('navPanelPinned', JSON.stringify(pinned));
  }, [pinned]);
  
  // Save collapsed portfolios state to localStorage
  useEffect(() => {
    localStorage.setItem('collapsedPortfolios', JSON.stringify(collapsedPortfolios));
  }, [collapsedPortfolios]);

  // Persist extra navigation items visibility
  useEffect(() => {
    localStorage.setItem('showExtraNavigationItems', JSON.stringify(showExtraNavigationItems));
  }, [showExtraNavigationItems]);

  // Fetch navigation display setting
  useEffect(() => {
    const fetchNavigationSetting = async () => {
      try {
        const response = await getSettings();
        if (response.data && response.data.show_portfolios_in_nav !== undefined) {
          console.log('NavigationPanel: Fetched setting from backend:', response.data.show_portfolios_in_nav);
          setShowPortfoliosInNav(response.data.show_portfolios_in_nav);
        }
      } catch (err) {
        console.error('Error fetching navigation setting:', err);
        // Keep default value (true) if there's an error
      }
    };
    
    fetchNavigationSetting();
    
    // Listen for setting changes from Settings page
    const handleSettingChange = (event) => {
      if (event.detail && event.detail.show_portfolios_in_nav !== undefined) {
        console.log('NavigationPanel: Received setting change event:', event.detail.show_portfolios_in_nav);
        setShowPortfoliosInNav(event.detail.show_portfolios_in_nav);
      }
    };
    
    window.addEventListener('navigationSettingChanged', handleSettingChange);
    
    return () => {
      window.removeEventListener('navigationSettingChanged', handleSettingChange);
    };
  }, []);

  // Listen for accounts data from AllAccounts page to avoid duplicate calculations
  useEffect(() => {
    const handleAccountsUpdated = (event) => {
      // console.log('[NavigationPanel] Received accounts data from AllAccounts page:', event.detail);
      // Only update accountsData if we have valid data, don't clear it when navigating away
      if (event.detail.accounts && event.detail.accounts.length > 0) {
        setAccountsData(event.detail.accounts);
      }
    };
    
    window.addEventListener('accountsUpdated', handleAccountsUpdated);
    
    return () => {
      window.removeEventListener('accountsUpdated', handleAccountsUpdated);
    };
  }, []);

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      // If the panel is not showing, there's no need to handle click outside
      if (!navPanelRef.current) {
        return;
      }
      
      // Check if the click is on the toggle button
      const isToggleButton = event.target.closest('.mobile-nav-toggle') || 
                             event.target.closest('.square-menu-btn') ||
                             event.target.classList.contains('bi-list') || 
                             event.target.classList.contains('bi-x-lg');
      
      // If click is outside panel and not on toggle button
      if (!navPanelRef.current.contains(event.target) && !isToggleButton) {
        if (isMobile && mobileVisible) {
          // For mobile, dispatch event to hide the mobile nav
          const mobileNavEvent = new CustomEvent('mobileNavChange', { 
            detail: { visible: false } 
          });
          window.dispatchEvent(mobileNavEvent);
        } else if (!isMobile && !collapsed && !pinned) {
          // For desktop, collapse the panel if it's not pinned
          setCollapsed(true);
          // Dispatch custom event to notify other components
          const navCollapseEvent = new CustomEvent('navCollapseChange', { 
            detail: { collapsed: true } 
          });
          window.dispatchEvent(navCollapseEvent);
        }
      }
    }

    // Use mousedown for better mobile compatibility
    document.addEventListener("mousedown", handleClickOutside);
    // Also add touchstart for mobile devices
    document.addEventListener("touchstart", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [collapsed, mobileVisible, isMobile, pinned]);

  const fetchPortfolios = useCallback(async () => {
    try {
      setError(''); // Clear any previous errors
      const response = await getPortfolios(userId);

      // Sort portfolios by total balance (descending) and accounts within each portfolio by balance (descending)
      const sortedPortfolios = response.data.map(portfolio => ({
        ...portfolio,
        accounts: portfolio.accounts ? portfolio.accounts.sort((a, b) => (b.balance || 0) - (a.balance || 0)) : []
      })).sort((a, b) => (b.balance || 0) - (a.balance || 0));

      setPortfolios(sortedPortfolios);
      // Broadcast portfolios to keep pages and nav in sync
      window.dispatchEvent(new CustomEvent('portfoliosUpdated', { detail: { portfolios: sortedPortfolios } }));
    } catch (err) {
      console.error('Error fetching portfolios:', err);

      // Provide more specific error messages based on error type
      let errorMessage = 'Failed to load portfolios';

      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        if (status === 401) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (status === 403) {
          errorMessage = 'Access denied. Please check your permissions.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again in a moment.';
        } else {
          errorMessage = `Failed to load portfolios: ${err.response.data?.error || 'Unknown error'}`;
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else {
        // Other error
        errorMessage = `Error: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
      setRetryLoading(false);
    }
  }, [userId]);

  // Fetch accounts data for consistent percentage calculations
  const fetchAccountsData = useCallback(async () => {
    try {
      const response = await getAllAccountsWithAggregate();
      // console.log('[NavigationPanel] Fetched accounts data for consistent calculations:', response.data);

      // New format - object with accounts array and aggregate all-time high data
      setAccountsData(response.data.accounts || []);
      setAggregateAllTimeHigh(response.data.aggregate_all_time_high || null);
    } catch (err) {
      console.error('[NavigationPanel] Error fetching accounts data:', err);
      // Don't set error state for this - it's not critical for basic functionality
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchPortfolios();
      fetchAccountsData(); // Fetch accounts data for consistent calculations
    }
  }, [userId, fetchPortfolios, fetchAccountsData]);

  // Sync with global portfoliosUpdated events
  useEffect(() => {
    const handlePortfoliosUpdated = (event) => {
      const incoming = event?.detail?.portfolios;
      if (!incoming || !Array.isArray(incoming)) return;
      // Apply the same sorting as local fetch for consistent UI
      const sorted = incoming.map(p => ({
        ...p,
        accounts: p.accounts ? [...p.accounts].sort((a, b) => (b.balance || 0) - (a.balance || 0)) : []
      })).sort((a, b) => (b.balance || 0) - (a.balance || 0));
      setPortfolios(sorted);
    };
    window.addEventListener('portfoliosUpdated', handlePortfoliosUpdated);
    return () => window.removeEventListener('portfoliosUpdated', handlePortfoliosUpdated);
  }, []);
  
  // Add WebSocket subscription to automatically refresh portfolio data when ticker prices change
  useEffect(() => {
    console.log('NavigationPanel: Setting up ticker update subscription');
    
    const unsubscribe = subscribeToTickerUpdates((data) => {
      // console.log('NavigationPanel: Received ticker update via WebSocket', {
      //   hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
      //   updateCount: data?.updated_tickers?.length || 0,
      //   hasLastUpdateTime: Boolean(data?.latest_update_time),
      //   isTimestampEvent: data?.message === 'Timestamp update event'
      // });
      
      // Only refresh portfolios if there are actual ticker price updates
      // Don't refresh for timestamp-only events to avoid excessive API calls
      if (data && data.updated_tickers && data.updated_tickers.length > 0) {
        console.log(`NavigationPanel: Refreshing portfolio data due to ${data.updated_tickers.length} ticker updates`);
        fetchPortfolios();
        fetchAccountsData(); // Also refresh accounts data to keep calculations consistent
      }
    });
    
    return () => {
      console.log('NavigationPanel: Cleaning up ticker update subscription');
      unsubscribe();
    };
  }, [fetchPortfolios, fetchAccountsData]);
  
  // Listen for account creation events to refresh the navigation panel
  useEffect(() => {
    const handleAccountCreated = () => {
      // Refresh portfolios when a new account is created
      fetchPortfolios();
      fetchAccountsData(); // Also refresh accounts data
    };
    
    const handleNameChanged = () => {
      // Refresh portfolios when a name is changed elsewhere in the app
      fetchPortfolios();
      fetchAccountsData(); // Also refresh accounts data
    };
    
    // Add event listeners
    window.addEventListener('accountCreated', handleAccountCreated);
    window.addEventListener('nameChanged', handleNameChanged);
    
    // Clean up the event listeners when component unmounts
    return () => {
      window.removeEventListener('accountCreated', handleAccountCreated);
      window.removeEventListener('nameChanged', handleNameChanged);
    };
  }, [fetchPortfolios, fetchAccountsData]);

  // Listen for completed transactions to refresh nav data (cash/progress)
  useEffect(() => {
    const handleTransactionCompleted = (event) => {
      // Optionally inspect event.detail.accountId if needed
      fetchPortfolios();
      fetchAccountsData(); // Also refresh accounts data
    };

    window.addEventListener('transactionCompleted', handleTransactionCompleted);
    return () => {
      window.removeEventListener('transactionCompleted', handleTransactionCompleted);
    };
  }, [fetchPortfolios, fetchAccountsData]);

  // Calculate total balance across all accounts
  const calculateTotalAccountsBalance = () => {
    // Use accounts data from AllAccounts page if available (more accurate)
    if (accountsData && accountsData.length > 0) {
      return accountsData.reduce((total, account) => total + (account.balance || 0), 0);
    }
    
    // Fallback to portfolios data
    if (!portfolios || portfolios.length === 0) return 0;
    
    return portfolios.reduce((total, portfolio) => {
      if (portfolio.accounts && portfolio.accounts.length > 0) {
        return total + portfolio.accounts.reduce((portfolioTotal, account) => {
          return portfolioTotal + (account.balance || 0);
        }, 0);
      }
      return total;
    }, 0);
  };

  // Calculate today's gain percentage across all portfolios using weighted average
  const calculateTodayGainPercentage = () => {
    // Use accounts data from AllAccounts page if available (more accurate)
    if (accountsData && accountsData.length > 0) {
      // Use the same calculation method as AllAccounts page for consistency
      const totalGainAmount = accountsData.reduce((sum, account) => sum + (account.today_gain_amount || 0), 0);
      const totalPreviousDayBalance = accountsData.reduce((sum, account) => sum + (account.previous_day_balance || 0), 0);
      
      // Use previous_day_balance for percentage calculation to match AllAccounts page
      const percentage = totalPreviousDayBalance > 0 ? (totalGainAmount / totalPreviousDayBalance) * 100 : 0;
      
      // Debug logging removed to reduce console spam
      // console.log('[NavigationPanel] Using optimized accounts data for percentage calculation:', {
      //   totalGainAmount,
      //   totalPreviousDayBalance,
      //   percentage: percentage.toFixed(2) + '%'
      // });
      
      return percentage;
    }
    
    // Fallback to portfolios data
    if (!portfolios || portfolios.length === 0) return 0;
    
    let totalWeightedGain = 0;
    let totalBalance = 0;
    
    portfolios.forEach(portfolio => {
      const portfolioBalance = portfolio.balance || 0;
      const portfolioTodayGainPercentage = portfolio.today_gain_percentage || 0;
      
      // Calculate weighted gain contribution
      totalWeightedGain += (portfolioTodayGainPercentage * portfolioBalance);
      totalBalance += portfolioBalance;
    });
    
    const percentage = totalBalance > 0 ? (totalWeightedGain / totalBalance) : 0;
    return percentage;
  };

  // Calculate total cash across all accounts
  const calculateTotalCash = () => {
    // Use accounts data from AllAccounts page if available (more accurate)
    if (accountsData && accountsData.length > 0) {
      return accountsData.reduce((sum, account) => sum + (account.cash || 0), 0);
    }
    
    // Fallback to portfolios data
    if (!portfolios || portfolios.length === 0) return 0;
    return portfolios.reduce((sum, p) => {
      const portfolioCash = p.accounts ? p.accounts.reduce((s, a) => s + (a.cash || 0), 0) : 0;
      return sum + portfolioCash;
    }, 0);
  };

  // Aggregate invested percentage across all accounts
  const calculateAggregateInvestedPercent = () => {
    const totalBal = calculateTotalAccountsBalance();
    const totalCash = calculateTotalCash();
    if (totalBal <= 0) return 0;
    const investedPct = 100 - ((totalCash / totalBal) * 100);
    return Math.max(0, Math.min(100, investedPct));
  };

  // Map market condition to target exposure percent
  const getMarketTargetPercent = (condition) => {
    const percent = getMarketConditionOption(condition).percent;
    return parseInt(percent, 10) || 0;
  };
  const filterAccountsByBalance = (accounts = []) => accounts.filter(account => (account.balance || 0) !== 0);
  const getFlatAccountsSortedByBalance = () => {
    const allAccounts = portfolios.flatMap(portfolio => (portfolio.accounts ? portfolio.accounts : []));
    return filterAccountsByBalance(allAccounts).sort((a, b) => (b.balance || 0) - (a.balance || 0));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleCollapse = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    
    // Dispatch custom event to notify other components
    const navCollapseEvent = new CustomEvent('navCollapseChange', { 
      detail: { collapsed: newCollapsedState } 
    });
    window.dispatchEvent(navCollapseEvent);
  };
  
  const togglePin = () => {
    const newPinnedState = !pinned;
    setPinned(newPinnedState);
    
    // If we're pinning, make sure panel is expanded
    if (newPinnedState && collapsed) {
      setCollapsed(false);
      // Dispatch custom event to notify other components
      const navCollapseEvent = new CustomEvent('navCollapseChange', { 
        detail: { collapsed: false } 
      });
      window.dispatchEvent(navCollapseEvent);
    }
  };
  
  // Toggle portfolio collapse
  const togglePortfolioCollapse = (portfolioId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setCollapsedPortfolios(prev => ({
      ...prev,
      [portfolioId]: !prev[portfolioId]
    }));
  };
  
  // Expand all portfolios
  const expandAllPortfolios = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Create an object with all portfolios set to false (expanded)
    const expandedState = {};
    portfolios.forEach(portfolio => {
      expandedState[portfolio.portfolio_id] = false;
    });
    
    setCollapsedPortfolios(expandedState);
  };
  
  // Collapse all portfolios
  const collapseAllPortfolios = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Create an object with all portfolios set to true (collapsed)
    const collapsedState = {};
    portfolios.forEach(portfolio => {
      collapsedState[portfolio.portfolio_id] = true;
    });
    
    setCollapsedPortfolios(collapsedState);
  };
  
  // Check if a portfolio is collapsed
  const isPortfolioCollapsed = (portfolioId) => {
    return !!collapsedPortfolios[portfolioId];
  };

  // Helper function for formatting balance with dollar sign
  const formatBalance = (value) => {
    return '$' + Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Helper function to format percentage
  const formatPercentage = (value) => {
    const percentage = Number(value || 0);
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(percentage);
    if (absValue < 0.01) {
      return '0.00%';
    }
    return `${percentage.toFixed(2)}%`;
  };

  // Helper function to determine text color based on value
  const getPercentageColor = (value) => {
    const percentage = Number(value || 0);
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(percentage);
    if (absValue < 0.01) {
      return 'text-success fw-bold'; // Show green for values close to zero
    }
    if (percentage > 0) return 'text-success fw-bold';
    if (percentage < 0) return 'text-danger fw-bold';
    return 'text-secondary';
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingPortfolioId(null);
    setEditingAccountId(null);
    setEditName('');
  };
  
  // Handle name change
  const handleNameChange = (e) => {
    setEditName(e.target.value);
  };
  
  // Save portfolio name change
  const savePortfolioName = async (portfolioId, e) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      return;
    }
    
    setEditLoading(true);
    
    try {
      await updatePortfolio(portfolioId, { name: editName.trim() });
      
      // Refresh portfolios data
      await fetchPortfolios();
      
      // Reset edit state
      setEditingPortfolioId(null);
      setEditName('');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('nameChanged'));
      
    } catch (err) {
      console.error('Error updating portfolio name:', err);
    } finally {
      setEditLoading(false);
    }
  };
  
  // Save account name change
  const saveAccountName = async (accountId, e) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      return;
    }
    
    setEditLoading(true);
    
    try {
      await updateAccount(accountId, { name: editName.trim() });
      
      // Refresh portfolios data
      await fetchPortfolios();
      
      // Reset edit state
      setEditingAccountId(null);
      setEditName('');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('nameChanged'));
      
    } catch (err) {
      console.error('Error updating account name:', err);
    } finally {
      setEditLoading(false);
    }
  };

  if (!userId) {
    return null;
  }

  if (loading) {
    return <div className="p-3">Loading...</div>;
  }

  // Helper function to retry loading portfolios
  const handleRetryPortfolios = () => {
    setRetryLoading(true);
    fetchPortfolios();
    fetchAccountsData(); // Also refresh accounts data
  };

  // Only show overlay backdrop when mobile nav is visible on small screens
  const showBackdrop = isMobile && mobileVisible && !collapsed;
  
  return (
    <>
      {showBackdrop && (
        <div 
          className="navigation-backdrop" 
          onClick={() => {
            // Dismiss the mobile nav when backdrop is clicked
            const mobileNavEvent = new CustomEvent('mobileNavChange', { 
              detail: { visible: false } 
            });
            window.dispatchEvent(mobileNavEvent);
          }}
        />
      )}
      <div
        ref={navPanelRef}
        className={`navigation-panel ${isMobile ? (mobileVisible ? 'floating show' : 'floating') : ''} ${collapsed ? 'collapsed' : ''} ${pinned ? 'pinned' : ''}`}
        style={{ 
          height: '100vh', 
          overflowY: 'auto',
          transition: 'width 0.3s ease, transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {!collapsed && (
          <div className="d-flex flex-column h-100">
            {/* Fixed header section */}
            <div className="flex-shrink-0">
              <div className="p-1 border-bottom pt-0 pb-1">
                <Link to="/dashboard" className="text-decoration-none text-dark d-block">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-grid me-2"></i>
                      <h5 className="mb-0">Portfolios</h5>
                    </div>
                    <div className="d-flex align-items-center">
                      <Button 
                        variant="light" 
                        size="sm" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePin();
                        }}
                        className="me-1"
                        aria-label={pinned ? "Unpin navigation" : "Pin navigation"}
                        title={pinned ? "Unpin navigation" : "Pin navigation"}
                      >
                        <i className={`bi ${pinned ? 'bi-pin-fill' : 'bi-pin'}`}></i>
                      </Button>
                      <Button 
                        variant="light" 
                        size="sm" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCollapse();
                        }}
                        className="me-0"
                        aria-label="Collapse navigation"
                      >
                        <i className={`bi bi-chevron-left`}></i>
                      </Button>
                    </div>
                  </div>
                </Link>
              </div>

              {/* Accounts row navigates to Accounts page (no submenu expansion) */}
              <div
                className="p-2 border-bottom"
                role="button"
                tabIndex={0}
                onClick={() => navigate('/all-accounts')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/all-accounts'); }}
                aria-expanded={false}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    {aggregateAllTimeHigh?.is_new_all_time_high_today ? (
                      <i className="bi bi-star-fill me-2 text-warning" title="New All-Time High Total Balance Today!"></i>
                    ) : (
                      <i className="bi bi-collection me-2"></i>
                    )}
                    <span>Accounts</span>
                  </div>
                  <div className="text-end d-flex flex-column align-items-end" style={{ flexBasis: '60%', paddingRight: '8px' }}>
                    <div className="fw-bold text-dark small d-flex align-items-baseline gap-2">
                      {formatBalance(calculateTotalAccountsBalance())}
                      <span className={`${getPercentageColor(calculateTodayGainPercentage())}`} style={{ fontSize: '0.8em' }} title="Total Today's Gain %">
                        ({formatPercentage(calculateTodayGainPercentage())})
                      </span>
                    </div>
                    {calculateTotalAccountsBalance() > 0 && (() => {
                      const investedPct = calculateAggregateInvestedPercent();
                      const cashPct = Math.max(0, Math.min(100, 100 - investedPct));
                      const targetPct = getMarketTargetPercent(marketCondition);
                      const overshoot = investedPct - targetPct;
                      const barStyle = { width: `${investedPct}%` };
                      if (overshoot > 10) {
                        barStyle.backgroundColor = '#dc3545'; // danger red
                      } else if (overshoot > 5) {
                        barStyle.backgroundColor = '#ffc107'; // warning yellow
                      }
                      return (
                        <div
                          className="mt-1 account-invested-progress"
                          style={{ height: '24px', position: 'relative' }}
                          title={`Invested: ${Math.round(investedPct)}% | Cash: ${Math.round(cashPct)}%`}
                        >
                          <div
                            className="account-invested-progress-bar"
                            style={barStyle}
                          />
                          <div
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{ pointerEvents: 'none', display: 'flex' }}
                          >
                            <div style={{ width: `${investedPct}%`, position: 'relative' }}>
                              {investedPct > 20 && (
                                <span
                                  className="d-inline-block"
                                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', textAlign: 'right' }}
                                >
                                  {`${Math.round(investedPct)}%`}
                                </span>
                              )}
                            </div>
                            <div style={{ width: `${cashPct}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {false && (
                <>
                  {/* All Tickers NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/tickers" className={`text-decoration-none ${isActive('/tickers') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-bar-chart-fill me-2"></i>
                        <span>Tickers</span>
                      </div>
                    </Link>
                  </div>

                  {/* Transactions NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/transactions" className={`text-decoration-none ${isActive('/transactions') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-receipt me-2"></i>
                        <span>Transactions</span>
                      </div>
                    </Link>
                  </div>

                  {/* Trades NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/trades" className={`text-decoration-none ${isActive('/trades') || location.pathname.startsWith('/trades/') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-graph-up me-2"></i>
                        <span>Trades</span>
                      </div>
                    </Link>
                  </div>

                  {/* Orders NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/orders" className={`text-decoration-none ${isActive('/orders') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-list-check me-2"></i>
                        <span>Orders</span>
                      </div>
                    </Link>
                  </div>

                  {/* Readylist NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/readylist" className={`text-decoration-none ${isActive('/readylist') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-star-fill me-2"></i>
                        <span>3-Ready</span>
                      </div>
                    </Link>
                  </div>

                  {/* Inverses NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/inverses" className={`text-decoration-none ${isActive('/inverses') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-arrow-left-right me-2"></i>
                        <span>Inverses</span>
                      </div>
                    </Link>
                  </div>

                  {/* HVol NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/hvol" className={`text-decoration-none ${isActive('/hvol') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-lightning-fill me-2"></i>
                        <span>HVol</span>
                      </div>
                    </Link>
                  </div>

                  {/* Minervini NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/minervini" className={`text-decoration-none ${isActive('/minervini') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-graph-up me-2"></i>
                        <span>Minervini</span>
                      </div>
                    </Link>
                  </div>

                  {/* Minervini NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/minervini" className={`text-decoration-none ${isActive('/minervini') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-graph-up me-2"></i>
                        <span>Minervini</span>
                      </div>
                    </Link>
                  </div>

                  {/* Watchlist NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/watchlist" className={`text-decoration-none ${isActive('/watchlist') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-star me-2"></i>
                        <span>2-Watch</span>
                      </div>
                    </Link>
                  </div>

                  {/* Ants NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/ants" className={`text-decoration-none ${isActive('/ants') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-bug me-2"></i>
                        <span>Ants</span>
                      </div>
                    </Link>
                  </div>

                  {/* NewIPOs NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/new-ipos" className={`text-decoration-none ${isActive('/new-ipos') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-rocket-takeoff me-2"></i>
                        <span>NewIPOs</span>
                      </div>
                    </Link>
                  </div>

                  {/* ETFs NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/etfs" className={`text-decoration-none ${isActive('/etfs') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-stack me-2"></i>
                        <span>ETFs</span>
                      </div>
                    </Link>
                  </div>

                  {/* Leaders NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/leaders" className={`text-decoration-none ${isActive('/leaders') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-trophy me-2"></i>
                        <span>Leaders</span>
                      </div>
                    </Link>
                  </div>

                  {/* VCP NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/vcp" className={`text-decoration-none ${isActive('/vcp') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-activity me-2"></i>
                        <span>1-VCP</span>
                      </div>
                    </Link>
                  </div>

                  {/* Basing NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/basing" className={`text-decoration-none ${isActive('/basing') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-layers me-2"></i>
                        <span>0-Basing</span>
                      </div>
                    </Link>
                  </div>

                  {/* EPS NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/eps" className={`text-decoration-none ${isActive('/eps') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-bar-chart-line me-2"></i>
                        <span>GapUPs</span>
                      </div>
                    </Link>
                  </div>

                  {/* ATHigh NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/athigh" className={`text-decoration-none ${isActive('/athigh') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-graph-up-arrow me-2"></i>
                        <span>ATHigh</span>
                      </div>
                    </Link>
                  </div>

                  {/* Turnarounds NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/turnarounds" className={`text-decoration-none ${isActive('/turnarounds') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-arrow-repeat me-2"></i>
                        <span>Turnarounds</span>
                      </div>
                    </Link>
                  </div>

                  {/* Cyclicals NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/cyclicals" className={`text-decoration-none ${isActive('/cyclicals') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-circle me-2"></i>
                        <span>Cyclicals</span>
                      </div>
                    </Link>
                  </div>

                  {/* GLeaders NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/gleaders" className={`text-decoration-none ${isActive('/gleaders') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-people me-2"></i>
                        <span>GLeaders</span>
                      </div>
                    </Link>
                  </div>

                  {/* Themes NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/themes" className={`text-decoration-none ${isActive('/themes') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-collection me-2"></i>
                        <span>Themes</span>
                      </div>
                    </Link>
                  </div>

                  {/* Screen NAV Link */}
                  <div className="p-2 border-bottom">
                    <Link to="/screen" className={`text-decoration-none ${isActive('/screen') ? 'text-primary' : 'text-dark'} d-block`}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-layout-text-window-reverse me-2"></i>
                        <span>Screen</span>
                      </div>
                    </Link>
                  </div>
                </>
              )}
            </div>
            
            {/* Scrollable portfolio section - fills all available space */}
            <div 
              className="flex-grow-1 overflow-auto"
              style={{ 
                minHeight: 0  // Allow flex-grow to work properly
              }}
            >
              {error ? (
                <div className="p-2">
                  <Alert variant="danger" className="mb-2">
                    <div className="d-flex flex-column">
                      <div className="mb-2">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRetryPortfolios}
                        disabled={retryLoading}
                        className="align-self-start"
                      >
                        <i className={`bi ${retryLoading ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'} me-1`}></i>
                        {retryLoading ? 'Retrying...' : 'Retry'}
                      </Button>
                    </div>
                  </Alert>
                  {retryLoading && (
                    <div className="text-center text-muted">
                      <i className="bi bi-arrow-clockwise spin me-2"></i>
                      Loading portfolios...
                    </div>
                  )}
                </div>
              ) : (
                <ListGroup variant="flush">
                  {showPortfoliosInNav ? (
                    // Show portfolios with grouped accounts
                    portfolios.map((portfolio) => (
                    <div key={portfolio.portfolio_id} className="portfolio-group">
                      {editingPortfolioId === portfolio.portfolio_id ? (
                        <div className="px-3 py-2 portfolio-item">
                          <Form onSubmit={(e) => savePortfolioName(portfolio.portfolio_id, e)}>
                            <div className="d-flex align-items-center">
                              <Form.Control
                                type="text"
                                value={editName}
                                onChange={handleNameChange}
                                autoFocus
                                size="sm"
                                disabled={editLoading}
                                onKeyDown={(e) => e.key === 'Escape' && cancelEditing()}
                                style={{ flexGrow: 1, minWidth: "180px" }}
                              />
                              <div className="ms-2">
                                <Button 
                                  variant="success" 
                                  size="sm" 
                                  type="submit"
                                  disabled={editLoading || !editName.trim()}
                                  className="me-1"
                                >
                                  <i className="bi bi-check"></i>
                                </Button>
                                <Button 
                                  variant="secondary" 
                                  size="sm"
                                  onClick={cancelEditing}
                                  disabled={editLoading}
                                >
                                  <i className="bi bi-x"></i>
                                </Button>
                              </div>
                            </div>
                          </Form>
                        </div>
                      ) : (
                        <ListGroup.Item
                          className={`d-flex justify-content-between align-items-center px-3 py-2 portfolio-item portfolio-header ${
                            isActive(`/portfolio/${portfolio.portfolio_id}`) ? 'active' : ''
                          } ${isMobile ? 'mobile-portfolio-item' : ''}`}
                          onClick={(e) => togglePortfolioCollapse(portfolio.portfolio_id, e)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') togglePortfolioCollapse(portfolio.portfolio_id, e); }}
                          title={isPortfolioCollapsed(portfolio.portfolio_id) ? 'Expand accounts' : 'Collapse accounts'}
                        >
                          <span style={{ flexBasis: isMobile ? '40%' : '50%', flexShrink: 1 }}>
                            <i className="bi bi-briefcase me-2"></i>
                            {portfolio.name}
                          </span>
                          <div className="d-flex justify-content-end align-items-center" style={{ flexBasis: isMobile ? '60%' : '50%' }}>
                            {/* Collapse/Expand accounts button hidden intentionally */}
                          </div>
                        </ListGroup.Item>
                      )}
                      
                      {filterAccountsByBalance(portfolio.accounts).length > 0 && (isMobile || !isPortfolioCollapsed(portfolio.portfolio_id)) && (
                        <div className="portfolio-accounts">
                          {filterAccountsByBalance(portfolio.accounts).map((account) => (
                            <React.Fragment key={account.account_id}>
                              {editingAccountId === account.account_id ? (
                                <div className="ps-3 px-3 py-2 account-item">
                                  <Form onSubmit={(e) => saveAccountName(account.account_id, e)}>
                                    <div className="d-flex align-items-center">
                                      <Form.Control
                                        type="text"
                                        value={editName}
                                        onChange={handleNameChange}
                                        autoFocus
                                        size="sm"
                                        disabled={editLoading}
                                        onKeyDown={(e) => e.key === 'Escape' && cancelEditing()}
                                        style={{ flexGrow: 1, minWidth: "180px" }}
                                      />
                                      <div className="ms-2">
                                        <Button 
                                          variant="success" 
                                          size="sm" 
                                          type="submit"
                                          disabled={editLoading || !editName.trim()}
                                          className="me-1"
                                        >
                                          <i className="bi bi-check"></i>
                                        </Button>
                                        <Button 
                                          variant="secondary" 
                                          size="sm"
                                          onClick={cancelEditing}
                                          disabled={editLoading}
                                        >
                                          <i className="bi bi-x"></i>
                                        </Button>
                                      </div>
                                    </div>
                                  </Form>
                                </div>
                              ) : (
                                <ListGroup.Item
                                  as={Link}
                                  to={`/account/${account.account_id}`}
                                  className={`ps-3 d-flex justify-content-between align-items-center px-3 py-2 account-item account-chip ${
                                    isActive(`/account/${account.account_id}`) ? 'active' : ''
                                  }`}
                                >
                                  <span style={{ flexBasis: '40%', flexShrink: 1 }}>
                                    {account.chart_all_time_high?.is_new_all_time_high_today ? (
                                      <i className="bi bi-star-fill me-2 text-warning" title="New All-Time High Balance Today!"></i>
                                    ) : (
                                      <i className="bi bi-wallet2 me-2"></i>
                                    )}
                                    <span className="account-name">{account.name}</span>
                                  </span>
                                  <div className="text-end d-flex flex-column align-items-end" style={{ 
                                    flexBasis: '60%', 
                                    paddingRight: '8px'
                                  }}>
                                    <div className="fw-bold small d-flex align-items-baseline gap-2">
                                      {formatBalance(account.balance)}
                                      {account.today_gain_percentage !== undefined ? (
                                        account.today_gain_percentage > 0 ? (
                                          <span className="text-success fw-bold" style={{ fontSize: '0.8em' }}>
                                            ({formatPercentage(account.today_gain_percentage)})
                                          </span>
                                        ) : account.today_gain_percentage < 0 ? (
                                          <span className="text-danger fw-bold" style={{ fontSize: '0.8em' }}>
                                            ({formatPercentage(account.today_gain_percentage)})
                                          </span>
                                        ) : (
                                          <span className="text-secondary fw-bold" style={{ fontSize: '0.8em' }}>
                                            ({formatPercentage(account.today_gain_percentage)})
                                          </span>
                                        )
                                      ) : (
                                        <span className="text-secondary fw-bold" style={{ fontSize: '0.8em' }}>(0.00%)</span>
                                      )}
                                    </div>

                                    {/* Invested vs Cash progress bar */}
                                    {account.balance > 0 && (() => {
                                      const investedPct = Math.max(0, Math.min(100, 100 - (((account.cash || 0) / account.balance) * 100)));
                                      const cashPct = Math.max(0, Math.min(100, (((account.cash || 0) / account.balance) * 100)));
                                      const targetPct = getMarketTargetPercent(marketCondition);
                                      const barStyle = { width: `${investedPct}%` };
                                      if (investedPct > targetPct) {
                                        barStyle.backgroundColor = '#ffc107'; // warning yellow when account exposure exceeds market indicator
                                      }
                                      return (
                                        <div 
                                          className="mt-1 account-invested-progress"
                                          style={{ height: '24px', position: 'relative' }}
                                          title={`Invested: ${Math.round(investedPct)}% | Cash: ${Math.round(cashPct)}%`}
                                        >
                                          <div 
                                            className="account-invested-progress-bar"
                                            style={barStyle}
                                          />
                                          <div
                                            className="position-absolute top-0 start-0 w-100 h-100"
                                            style={{ pointerEvents: 'none', display: 'flex' }}
                                          >
                                            <div style={{ width: `${investedPct}%`, position: 'relative' }}>
                                              {investedPct > 20 && (
                                                <span
                                                  className="d-inline-block"
                                                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', textAlign: 'right' }}
                                                >
                                                  {`${Math.round(investedPct)}%`}
                                                </span>
                                              )}
                                            </div>
                                            <div style={{ width: `${cashPct}%` }} />
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </ListGroup.Item>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                    ))
                  ) : (
                    // Show flat list of all accounts
                    getFlatAccountsSortedByBalance().map(account => (
                        <React.Fragment key={account.account_id}>
                          {editingAccountId === account.account_id ? (
                            <div className="px-3 py-2 account-item">
                              <Form onSubmit={(e) => saveAccountName(account.account_id, e)}>
                                <div className="d-flex align-items-center">
                                  <Form.Control
                                    type="text"
                                    value={editName}
                                    onChange={handleNameChange}
                                    autoFocus
                                    size="sm"
                                    disabled={editLoading}
                                    onKeyDown={(e) => e.key === 'Escape' && cancelEditing()}
                                    style={{ flexGrow: 1, minWidth: "180px" }}
                                  />
                                  <div className="ms-2">
                                    <Button 
                                      variant="success" 
                                      size="sm" 
                                      type="submit"
                                      disabled={editLoading || !editName.trim()}
                                      className="me-1"
                                    >
                                      <i className="bi bi-check"></i>
                                    </Button>
                                    <Button 
                                      variant="secondary" 
                                      size="sm"
                                      onClick={cancelEditing}
                                      disabled={editLoading}
                                    >
                                      <i className="bi bi-x"></i>
                                    </Button>
                                  </div>
                                </div>
                              </Form>
                            </div>
                          ) : (
                            <ListGroup.Item
                              as={Link}
                              to={`/account/${account.account_id}`}
                              className={`d-flex justify-content-between align-items-center px-3 py-2 account-item account-chip ${
                                isActive(`/account/${account.account_id}`) ? 'active' : ''
                              }`}
                            >
                              <span style={{ flexBasis: '40%', flexShrink: 1 }}>
                                {account.chart_all_time_high?.is_new_all_time_high_today ? (
                                  <i className="bi bi-star-fill me-2 text-warning" title="New All-Time High Balance Today!"></i>
                                ) : (
                                  <i className="bi bi-wallet2 me-2"></i>
                                )}
                                <span className="account-name">{account.name}</span>
                              </span>
                              <div className="text-end d-flex flex-column align-items-end" style={{ 
                                flexBasis: '60%', 
                                paddingRight: '8px'
                              }}>
                                <div className="fw-bold small d-flex align-items-baseline gap-2">
                                  {formatBalance(account.balance)}
                                  {account.today_gain_percentage !== undefined ? (
                                    account.today_gain_percentage > 0 ? (
                                      <span className="text-success fw-bold" style={{ fontSize: '0.8em' }}>
                                        ({formatPercentage(account.today_gain_percentage)})
                                      </span>
                                    ) : account.today_gain_percentage < 0 ? (
                                      <span className="text-danger fw-bold" style={{ fontSize: '0.8em' }}>
                                        ({formatPercentage(account.today_gain_percentage)})
                                      </span>
                                    ) : (
                                      <span className="text-secondary fw-bold" style={{ fontSize: '0.8em' }}>
                                        ({formatPercentage(account.today_gain_percentage)})
                                      </span>
                                    )
                                  ) : (
                                    <span className="text-secondary fw-bold" style={{ fontSize: '0.8em' }}>(0.00%)</span>
                                  )}
                                </div>

                                {/* Invested vs Cash progress bar */}
                                {account.balance > 0 && (() => {
                                  const investedPct = Math.max(0, Math.min(100, 100 - (((account.cash || 0) / account.balance) * 100)));
                                  const cashPct = Math.max(0, Math.min(100, (((account.cash || 0) / account.balance) * 100)));
                                  const targetPct = getMarketTargetPercent(marketCondition);
                                  const barStyle = { width: `${investedPct}%` };
                                  if (investedPct > targetPct) {
                                    barStyle.backgroundColor = '#ffc107'; // warning yellow when account exposure exceeds market indicator
                                  }
                                  return (
                                    <div 
                                      className="mt-1 account-invested-progress"
                                      style={{ height: '24px', position: 'relative' }}
                                      title={`Invested: ${Math.round(investedPct)}% | Cash: ${Math.round(cashPct)}%`}
                                    >
                                      <div 
                                        className="account-invested-progress-bar"
                                        style={barStyle}
                                      />
                                      <div
                                        className="position-absolute top-0 start-0 w-100 h-100"
                                        style={{ pointerEvents: 'none', display: 'flex' }}
                                      >
                                        <div style={{ width: `${investedPct}%`, position: 'relative' }}>
                                          {investedPct > 20 && (
                                            <span
                                              className="d-inline-block"
                                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', textAlign: 'right' }}
                                            >
                                              {`${Math.round(investedPct)}%`}
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ width: `${cashPct}%` }} />
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </ListGroup.Item>
                          )}
                        </React.Fragment>
                    ))
                  )}
                </ListGroup>
              )}
            </div>
            
            {/* Fixed bottom section for buttons and version info */}
            <div className="flex-shrink-0 border-top">
              {/* Expand/Collapse All buttons - only show when portfolios are displayed */}
              {showPortfoliosInNav && (
                <div className="p-2 d-flex justify-content-between">
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={expandAllPortfolios}
                    className="flex-grow-1 me-1"
                  >
                    <i className="bi bi-arrows-expand me-1"></i>
                    Expand All
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={collapseAllPortfolios}
                    className="flex-grow-1 ms-1"
                  >
                    <i className="bi bi-arrows-collapse me-1"></i>
                    Collapse All
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {collapsed && (
        <div className="menu-button-container">
          <Button 
            variant="light" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleCollapse();
            }}
            className="square-menu-btn position-fixed"
            aria-label="Expand navigation"
            title="Expand navigation"
            style={{
              width: '44px',
              height: '44px',
              padding: '0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              top: '66px', 
              left: '10px',
              zIndex: 1001
            }}
          >
            <i className="bi bi-list" style={{ fontSize: '1.5rem' }}></i>
          </Button>
        </div>
      )}
    </>
  );
}

export default NavigationPanel; 
